import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import api from "../../utils/api";
import { toLocalizedStatus, toLocalizedNumber } from "../../utils/localeUtils";
import {
  FaExclamationTriangle,
  FaClock,
  FaSearch,
  FaCheckCircle,
  FaChevronRight,
  FaInbox,
  FaShieldAlt,
} from "react-icons/fa";
import Pagination from "../../components/Pagination";
import "./MyReports.css";

const PER_PAGE = 10;

const MyReports = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);

  const REASON_LABELS = {
    marked_delivered_not_received: t(
      "myReports.reasonMarkedDeliveredNotReceived",
    ),
    marked_paid_not_delivered: t("myReports.reasonMarkedPaidNotDelivered"),
    wrong_items: t("myReports.reasonWrongItems"),
    quality_issue: t("myReports.reasonQualityIssue"),
    other: t("myReports.reasonOther"),
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await api.get("/reports");
      setReports(res.data?.reports || []);
    } catch (err) {
      console.error("Error fetching reports:", err);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const getReasonLabel = (reason) => REASON_LABELS[reason] || reason || "—";

  const getStatusIcon = (status) => {
    if (status === "pending") return <FaClock />;
    if (status === "under_review") return <FaSearch />;
    if (status === "resolved") return <FaCheckCircle />;
    return <FaExclamationTriangle />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return t("myReports.today");
    if (diffDays === 1) return t("myReports.yesterday");
    if (diffDays < 30) return t("myReports.daysAgo", { days: diffDays });
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const tabs = [
    { key: "all", label: t("myReports.tabAll") },
    { key: "pending", label: t("myReports.tabPending") },
    { key: "under_review", label: t("myReports.tabUnderReview") },
    { key: "resolved", label: t("myReports.tabResolved") },
  ];

  const counts = {
    all: reports.length,
    pending: reports.filter((r) => r.status === "pending").length,
    under_review: reports.filter((r) => r.status === "under_review").length,
    resolved: reports.filter((r) => r.status === "resolved").length,
  };

  const filteredReports =
    activeTab === "all"
      ? reports
      : reports.filter((r) => r.status === activeTab);

  const totalPages = Math.ceil(filteredReports.length / PER_PAGE);
  const pageReports = filteredReports.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  return (
    <div className="mrp-page">
      <Header />

      <div className="mrp-hero">
        <div className="mrp-hero-inner">
          <div className="mrp-hero-icon-wrap">
            <FaShieldAlt className="mrp-hero-icon" />
          </div>
          <div className="mrp-hero-text">
            <h1 className="mrp-hero-title">{t("myReports.title")}</h1>
            <p className="mrp-hero-sub">{t("myReports.subtitle")}</p>
          </div>
        </div>
      </div>

      <div className="mrp-body">
        {loading ? (
          <div className="mrp-loading">
            <div className="mrp-loading-dots">
              <span />
              <span />
              <span />
            </div>
            <p>{t("myReports.loading")}</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="mrp-empty">
            <div className="mrp-empty-icon-wrap">
              <FaInbox className="mrp-empty-icon" />
            </div>
            <h2 className="mrp-empty-heading">{t("myReports.noReports")}</h2>
            <p className="mrp-empty-text">{t("myReports.noReportsHint")}</p>
            <Link to="/orders" className="mrp-empty-btn">
              {t("myReports.viewMyOrders")}
            </Link>
          </div>
        ) : (
          <>
            {/* Summary Chips */}
            <div className="mrp-summary">
              <div className="mrp-chip mrp-chip--total">
                <span className="mrp-chip-num">
                  {toLocalizedNumber(reports.length)}
                </span>
                <span className="mrp-chip-label">
                  {t("myReports.totalReports")}
                </span>
              </div>
              <div className="mrp-chip mrp-chip--pending">
                <FaClock />
                <span className="mrp-chip-num">
                  {toLocalizedNumber(counts.pending)}
                </span>
                <span className="mrp-chip-label">
                  {t("myReports.tabPending")}
                </span>
              </div>
              <div className="mrp-chip mrp-chip--review">
                <FaSearch />
                <span className="mrp-chip-num">
                  {toLocalizedNumber(counts.under_review)}
                </span>
                <span className="mrp-chip-label">
                  {t("myReports.tabUnderReview")}
                </span>
              </div>
              <div className="mrp-chip mrp-chip--resolved">
                <FaCheckCircle />
                <span className="mrp-chip-num">
                  {toLocalizedNumber(counts.resolved)}
                </span>
                <span className="mrp-chip-label">
                  {t("myReports.tabResolved")}
                </span>
              </div>
            </div>

            {/* Tabs */}
            <div className="mrp-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  className={`mrp-tab ${activeTab === tab.key ? "mrp-tab--active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  <span className="mrp-tab-count">
                    {toLocalizedNumber(counts[tab.key])}
                  </span>
                </button>
              ))}
            </div>

            {/* Report Cards */}
            {filteredReports.length === 0 ? (
              <div className="mrp-no-match">
                <p>{t("myReports.noMatch")}</p>
              </div>
            ) : (
              <div className="mrp-list">
                {pageReports.map((report) => (
                  <div
                    key={report._id}
                    className={`mrp-card mrp-card--${report.status}`}
                  >
                    <div className="mrp-card-status-strip" />
                    <div className="mrp-card-inner">
                      <div className="mrp-card-top">
                        <div
                          className={`mrp-card-status-icon mrp-card-status-icon--${report.status}`}
                        >
                          {getStatusIcon(report.status)}
                        </div>
                        <div className="mrp-card-top-info">
                          <span className="mrp-card-reason">
                            {getReasonLabel(report.reason)}
                          </span>
                          <span className="mrp-card-date">
                            {formatDate(report.createdAt)}
                          </span>
                        </div>
                        <span
                          className={`mrp-badge mrp-badge--${report.status}`}
                        >
                          {toLocalizedStatus(report.status, t)}
                        </span>
                      </div>

                      {report.description && (
                        <p className="mrp-card-desc">{report.description}</p>
                      )}

                      {report.adminNotes && (
                        <div className="mrp-card-admin-note">
                          <strong>{t("myReports.adminNote")}</strong>
                          <p>{report.adminNotes}</p>
                        </div>
                      )}

                      <div className="mrp-card-footer">
                        <Link
                          to={`/order/${report.order?._id}`}
                          className="mrp-card-order-link"
                        >
                          {t("myReports.orderLabel")} #
                          {report.order?._id?.slice(-8) || "—"}
                          <FaChevronRight className="mrp-card-chevron" />
                        </Link>
                        {report.resolvedAt && (
                          <span className="mrp-card-resolved-date">
                            {t("myReports.resolved", {
                              date: new Date(
                                report.resolvedAt,
                              ).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                              }),
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MyReports;
